import {db} from '../db';
import GamesObject from './GamesObject';
import Template from './Template';
import Events from './Events';
import Benchmarks from './Benchmarks';
import firebase from 'firebase/app';
import 'firebase/firestore';
import Achievements from "./Achievements";


function err(e) {
    return { success: false, error: e };
}

/* Create a Response object for the tester assigned to this survey */
async function assignSurvey(sID, testerID) {
    return new Promise((resolve, reject) => {
        db.collection("Response").where("sID", "==", sID).where("testerID", "==", testerID).get()
        .then(querySnapshot => {
            if (!querySnapshot.empty) {
                console.log("Response already exists");
                resolve({success: false});
            } else {
                console.log("Assigning " + testerID + " to survey " + sID)
                db.collection("Response").add({
                    sID: sID,
                    testerID: testerID,
                    status: 0,
                    responses: null,
                })
                .then((docRef) => {
                    // console.log("Response created with ID: ", docRef.id);
                    Benchmarks.recordAssignment(sID, testerID);
                    resolve({success: true});
                })
                .catch(e => reject(err(e)))
            }
        })
        .catch(e => reject(err(e)))
    })
}

async function assignSurveyToAll(sID, gameID){
    return new Promise((resolve, reject) => {
        GamesObject.getProject(gameID).then(async function(res) {
            if (!res.success) {
                return reject({success: false, error: "Project does not exist"})
            }
                
            let testerIDs = res.data.testerIds
            await Promise.all(testerIDs.map(testerID => assignSurvey(sID, testerID)));
            await Template.setSurveyStatus(sID, 1);
            return resolve({success: true})
        }).catch(err => reject({success: false, error: err}))
    })
}

// increment surveysCompleted
async function incrementSurveysCompleted(testerID) {
    return new Promise((resolve, reject) => {
        db.collection("PlayTester").doc(testerID).get()
            .then(async doc => {
                if (!doc.exists) {
                    reject({ success: false, error: "User does not exist" });
                    return;
                }

                // Extract and update tester data
                let testerData = doc.data();
                testerData.surveysCompleted += 1;

                // Check if the achievement threshold is hit
                let isComplete = await Achievements.isComplete("Submit Surveys", testerData.surveysCompleted);

                // Update achievement count and Firestore document
                Achievements.setAchievementCount(testerData)
                    .then(achievementCount => {
                        db.collection("PlayTester").doc(testerID).update({
                            surveysCompleted: testerData.surveysCompleted,
                            achievementsCompleted: achievementCount,
                        });
                    })
                    .then(() => {
                        console.log("Survey count incremented successfully");
                        resolve({ success: true, achieved: isComplete, val: testerData.surveysCompleted });
                    })
                    .catch(error => {
                        console.error("Error updating PlayTester document:", error);
                        reject({ success: false, error });
                    });
            })
            .catch(error => {
                console.error("Error reading PlayTester document:", error);
                reject({ success: false, error });
            });
    });
}

async function sendSurveyCompletedNotification(sID, testerID) {
  try {
    // 1. Load survey info
    const surveyDoc = await db.collection("Survey").doc(sID).get();
    const surveyData = surveyDoc.data();
    const surveyName = surveyData.name || surveyData.surveyName;

    // 2. Load game info (to find developer)
    const gameId = surveyData.gameID;
    const gameDoc = await db.collection("Game").doc(gameId).get();
    const gameData = gameDoc.data();

    const developerId =
      gameData.developerID ||
      gameData.devID ||
      gameData.ownerID;

    if (!developerId) return;

    // 3. Load tester info
    const testerDoc = await db.collection("User").doc(testerID).get();
    const testerData = testerDoc.data();

    const testerName =
      testerData.nickName ||
      testerData.userName ||
      "Tester";

    const testerAvatar = testerData.img || null;

    // 4. Build notification object
    const notification = {
      type: "surveySubmitted",
      testerId: testerID,
      testerName,
      testerAvatar,
      projectId: gameId,
      projectName: gameData.gameName,
      projectImg: gameData.gameImage || null,
      surveyId: sID,
      surveyName,
      unread: true,
      timestamp: new Date()
    };

    // 5. Push to developer inbox
    await db
      .collection("UserNotifications")
      .doc(developerId)
      .collection("Notifications")
      .add(notification);

    console.log("📩 Survey submission notification sent to developer:", developerId);

  } catch (error) {
    console.error("❌ Failed to send survey notification", error);
  }
}
export async function sendPlaySessionCompletedNotification(playSessionId, testerID) {
  try {
    // 1. Load playsession
    const sessionDoc = await db.collection("PlaySession").doc(playSessionId).get();
    const session = sessionDoc.data();
    if (!session) return;

    const gameId = session.gameId;

    // 2. Load project
    const gameDoc = await db.collection("Game").doc(gameId).get();
    const gameData = gameDoc.data();
    if (!gameData) return;

    const developerId =
      gameData.developerID ||
      gameData.devID ||
      gameData.ownerID;

    if (!developerId) return;

    const projectName = gameData.gameName || "Project";
    const projectImg = gameData.gameImage || null;

    // 3. Load tester
    const testerDoc = await db.collection("User").doc(testerID).get();
    const testerData = testerDoc.data();
    if (!testerData) return;

    const testerName =
      testerData.nickName ||
      testerData.userName ||
      "Tester";
    const testerAvatar = testerData.img || null;

    // 4. Build notification
    const notification = {
      type: "playSessionCompleted",

      testerId: testerID,
      testerName,
      testerAvatar,

      projectId: gameId,
      projectName,
      projectImg,

      playSessionId,

      unread: true,
      timestamp: new Date(),
    };

    // 5. Push to developer inbox
    await db
      .collection("UserNotifications")
      .doc(developerId)
      .collection("Notifications")
      .add(notification);

    console.log("📩 PlaySession completed notification sent to:", developerId);

  } catch (e) {
    console.error("❌ Failed to send playSessionCompleted notification:", e);
  }
}
/* Saves a tester's response to a survey
   Once all responses to a survey are completed, sets survey status to 2 ("closed")
*/

async function saveResponses(sID, testerID, responses) {
  return new Promise((resolve, reject) => {
    db.collection("Response")
      .where("sID", "==", sID)
      .where("testerID", "==", testerID)
      .get()
      .then(async (querySnapshot) => {
        console.log("HI");

        // ----------------------------------------------------
        // CASE 1: First time submitting → create Response
        // ----------------------------------------------------
        if (querySnapshot.empty) {
          try {
            await db.collection("Response").add({
              sID,
              testerID,
              status: 1,
              responses,
            });

            // 🔔 Send notification on first submission
            await sendSurveyCompletedNotification(sID, testerID);

            Benchmarks.recordResponse(sID, testerID, responses);
            Events.logEvent("survey.submitted", {
              surveyId: sID,
              testerId: testerID,
              answers: Array.isArray(responses) ? responses.length : null,
            });
            resolve({ success: true });
          } catch (e) {
            reject(err(e));
          }
          return;
        }

        // ----------------------------------------------------
        // CASE 2: Response exists → update Response
        // ----------------------------------------------------
        const doc = querySnapshot.docs[0];
        const rID = doc.id;

        db.collection("Response")
          .doc(rID)
          .update({
            responses,
            status: 1,
          })
          .then(async () => {
            // 🔔 Send notification on update too
            await sendSurveyCompletedNotification(sID, testerID);

            // Continue original logic
            db.collection("Response")
              .where("sID", "==", sID)
              .where("status", "==", 1)
              .get()
              .then((responseQuery) => {
                getResponsesBySurvey(sID)
                  .then((res) => {
                    let res_data = res.data;
                    let testerList = res_data.map((t) => t.testerID);

                    if (testerList.length === responseQuery.size) {
                      Template.setSurveyStatus(sID, 2);
                    }

                    Benchmarks.recordResponse(sID, testerID, responses);
            Events.logEvent("survey.submitted", {
                      surveyId: sID,
                      testerId: testerID,
                      answers: Array.isArray(responses) ? responses.length : null,
                    });
                    resolve({ success: true });
                  })
                  .catch((e) => reject(err(e)));
              })
              .catch((e) => reject(err(e)));
          })
          .catch((e) => reject(err(e)));
      })
      .catch((e) => reject(err(e)));
  });
}


async function getResponsesByTester(testerID) {
    return new Promise(async (resolve, reject) => {
        await db.collection("Response").where("testerID", "==", testerID).get()
        .then(querySnapshot => {
            if (querySnapshot.empty) { // this testerID does not have any response yet, send empty array
                resolve({success: true, data: []});
            } else {
                let result = [];
                querySnapshot.forEach(doc => {
                    let response = {
                        rID : doc.id,
                        sID : doc.data().sID,
                        testerID : doc.data().testerID,
                        status : doc.data().status,
                        responses : doc.data().responses,
                    }
                    result.push(response);
                })
                // console.log("result: ", result);
                resolve({success: true, data: result});
            }
        })
        .catch(e => reject(err(e)));
    })
}

async function getResponsesBySurvey(sID) {
    return new Promise(async (resolve, reject) => {
        await db.collection("Response").where("sID", "==", sID).get()
        .then(querySnapshot => {
            if (querySnapshot.empty) { // this sID does not have any response yet, send empty array
                resolve({success: true, data: []});
            } else {
                let result = [];
                querySnapshot.forEach(doc => {
                    let response = {
                        rID : doc.id,
                        sID : doc.data().sID,
                        testerID : doc.data().testerID,
                        status : doc.data().status,
                        responses : doc.data().responses,
                    }
                    result.push(response);
                })
                // console.log("result: ", result);
                resolve({success: true, data: result});
            }
        })
        .catch(e => reject(err(e)));
    })
}

async function getCompletedResponses(sID) {
    return new Promise(async (resolve, reject) => {
        await db.collection("Response").where("sID", "==", sID).where("status", "==", 1).get()
        .then(querySnapshot => {
            if (querySnapshot.empty) { // this sID does not have any response yet, send empty array
                resolve({success: true, data: []});
            } else {
                let result = [];
                querySnapshot.forEach(doc => {
                    let response = {
                        rID : doc.id,
                        sID : doc.data().sID,
                        testerID : doc.data().testerID,
                        status : doc.data().status,
                        responses : doc.data().responses,
                    }
                    result.push(response);
                })
                // console.log("result: ", result);
                resolve({success: true, data: result});
            }
        })
        .catch(e => reject(err(e)));
    })
}
/* 
  Returns all unfinished surveys (published = 1) for a tester and project.
  Includes:
  - Surveys with NO response (never started)
  - Surveys with response status == 0 (incomplete)
*/
async function getUnfinishedSurveysByTesterProject(testerID, gameID) {
  return new Promise(async (resolve, reject) => {
    try {
      // 1. Get all surveys under this project that are published
      let surveyResult = await Template.getSurveysByGameID(gameID);
      let allPublishedSurveys = surveyResult.data.filter(s => s.status == 1);

      if (!allPublishedSurveys.length) {
        resolve({ success: true, data: [] });
        return;
      }

      let surveyIDs = allPublishedSurveys.map(s => s.sID);

      // 2. Get tester's responses for these surveys
      let responseSnapshot = await db
        .collection("Response")
        .where("testerID", "==", testerID)
        .where("sID", "in", surveyIDs)
        .get();

      // Convert responses into a map
      let responseMap = {};
      responseSnapshot.forEach(doc => {
        let data = doc.data();
        responseMap[data.sID] = data.status;  // 0 or 1
      });

      // 3. Find surveys that tester hasn't completed
      let unfinished = allPublishedSurveys.filter(survey => {
        let status = responseMap[survey.sID];
        return status !== 1; // includes undefined or 0
      });

      resolve({ success: true, data: unfinished });

    } catch (e) {
      reject(err(e));
    }
  });
}

/* Returns all incomplete Response objects (status == 0) for a specific tester and project */
async function getIncompleteResponsesByTesterProject(testerID, gameID) {
    return new Promise(async (resolve, reject) => {
        // get surveys that are under the given project
        let surveyResult = await Template.getSurveysByGameID(gameID);
        let surveyIDs = surveyResult.data.filter(survey => survey.status == 1).map(survey => survey.sID)
        if (!surveyIDs || !surveyIDs.length) {
            resolve({success: true, data: []})
            return;
        }
        await db.collection("Response").where("testerID", "==", testerID).where("sID", "in", surveyIDs).where("status", "==", 0).get()
        .then(querySnapshot => {
            if (querySnapshot.empty) { // no corresponding response yet, send empty array
                resolve({success: true, data: []});
            } else {
                let result = [];
                querySnapshot.forEach(doc => {
                    let response = {
                        rID : doc.id,
                        sID : doc.data().sID,
                        testerID : doc.data().testerID,
                        status : doc.data().status,
                        responses : doc.data().responses,
                    }
                    result.push(response);
                })
                // console.log("result: ", result);
                resolve({success: true, data: result});
            }
        })
        .catch(e => reject(err(e)));
    })
}

/* Returns all incomplete Surveys (status == 0) for a specific tester and project */
async function getIncompleteSurveysByTesterProject(testerID, gameID) {
    return new Promise(async (resolve, reject) => {
        // get surveys that are under the given project
        let surveyResult = await Template.getSurveysByGameID(gameID);
        let surveyIDs = surveyResult.data.filter(survey => survey.status == 1).map(survey => survey.sID)
        if (!surveyIDs || !surveyIDs.length) {
            resolve({success: true, data: []})
            return;
        }
        await db.collection("Response").where("testerID", "==", testerID).where("sID", "in", surveyIDs).where("status", "==", 0).get()
        .then(querySnapshot => {
            if (querySnapshot.empty) { // no corresponding response yet, send empty array
                resolve({success: true, data: []});
            } else {
                let incompleteSurveyIds = []
                querySnapshot.forEach(doc => {
                    incompleteSurveyIds.push(doc.data().sID);
                })
                let result = surveyResult.data.filter(survey => incompleteSurveyIds.indexOf(survey.sID) != -1)
                console.log("result: ", result);
                
                resolve({success: true, data: result});
            }
        })
        .catch(e => reject(err(e)));
    })
}

async function deleteResponses(sID, testerID) {
    return new Promise((resolve, reject) => {
        db.collection("Response").where("sID", "==", sID).where("testerID", "==", testerID).get()
        .then(querySnapshot => {
            if (querySnapshot.empty) {
                console.log("Could not find response with given sID and testerID");
                resolve({success: false});
            } else {
                querySnapshot.forEach((doc) => {
                    let rID = doc.id
                    // console.log("Delete id: ", rID);
                    db.collection("Response").doc(rID).delete()
                    .then(() => {
                        // console.log("Delete response with id ", rID);
                        resolve({success: true});
                    })
                    .catch(e => reject(err(e)))
                })
            }
        })
        .catch(e => reject(err(e)))
    })
}

export default {
    assignSurvey,
    assignSurveyToAll,
    saveResponses,
    getResponsesByTester,
    getResponsesBySurvey,
    getCompletedResponses,
    getIncompleteResponsesByTesterProject,
    getIncompleteSurveysByTesterProject,
    deleteResponses, 
    incrementSurveysCompleted,
    getUnfinishedSurveysByTesterProject
}


// WEBPACK FOOTER //
// ./src/Firestore/Response.js